import { AlertTriangle } from "lucide-react";

/**
 * One sentence, two surfaces: the suite header's disabled-run tooltip and the
 * Computer-environment section's notice band. Shared so a copy edit can't
 * leave one of them stale.
 */
export const EVAL_SANDBOX_CLOUD_UNREACHABLE_MESSAGE =
  "This suite pins a sandbox image, but this inspector can't run Sphinx cloud sandboxes.";

/**
 * Replay-specific variant: a replay provisions from the RUN's frozen snapshot
 * pin, which outlives clearing the live suite pin.
 */
export const EVAL_REPLAY_SNAPSHOT_CLOUD_UNREACHABLE_MESSAGE =
  "This run replays from its pinned sandbox image, but this inspector can't run Sphinx cloud sandboxes.";

/**
 * Amber preflight band for a cloud-only surface whose sandbox execution this
 * inspector cannot provide (`ephemeralCloudAvailable === false`) — shown
 * BEFORE a run is started, so the user isn't invited into a known failure.
 *
 * Tone contract mirrors `ComputersUnavailableMessage`: name the situation in
 * product terms; never instruct the user to set server environment variables.
 * Visual pattern mirrors the User Testing environment-error band.
 */
export function CloudUnreachableNotice({
  message,
  detail,
  "data-testid": testId,
}: {
  message: string;
  detail?: string;
  "data-testid"?: string;
}) {
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3"
      data-testid={testId ?? "cloud-unreachable-notice"}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500" />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-foreground">{message}</p>
        {detail ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
        ) : null}
      </div>
    </div>
  );
}
